import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import quoteService from '../../services/quote.service';
import { saveAs } from '../../utils/saveFile';

/**
 * Export Quotes to Salesforce (V1 salesforce_quote.pl exportsalesforcereport).
 * Pick a begin-date; the server returns a CSV of quotes changed since then that
 * carry a linked SF account, and stamps salesforceexportdatetime on each.
 */
const SalesforceExportPage: React.FC = () => {
  const navigate = useNavigate();
  const [beginDate, setBeginDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onExport = async () => {
    if (!beginDate) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      const saved = await saveAs(`salesforce_export_${beginDate}.csv`, () =>
        quoteService.exportSalesforce(beginDate),
      );
      setDone(saved);
    } catch (e: unknown) {
      // The error body is a Blob (responseType:'blob'); read it for the detail.
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          setError(parsed?.detail || 'Export failed.');
        } catch {
          setError('Export failed.');
        }
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-[18px] max-w-[640px]">
      <button
        type="button"
        onClick={() => navigate('/order-management/quotes')}
        className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-blue-600 hover:underline mb-3"
      >
        <ArrowLeftIcon className="h-3.5 w-3.5" /> Back to Quotes
      </button>

      <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
          <ArrowDownTrayIcon className="h-4 w-4 text-blue-600" />
          <h2 className="m-0 text-[0.8125rem] font-bold text-slate-900 dark:text-gray-100">Export Quotes to Salesforce</h2>
        </div>

        <div className="px-4 py-4 flex flex-col gap-3">
          <p className="text-[0.75rem] text-slate-500 dark:text-gray-400 m-0">
            Downloads a CSV of quotes changed on or after the begin-date that have a linked
            Salesforce account, and stamps each revision's export date.
          </p>

          <div className="flex items-center gap-2">
            <label className="text-[0.75rem] font-medium text-slate-700 dark:text-gray-200" htmlFor="sf-begin-date">Begin date</label>
            <input
              id="sf-begin-date"
              type="date"
              aria-label="Begin date"
              value={beginDate}
              onChange={(e) => { setBeginDate(e.target.value); setDone(false); setError(null); }}
              className="border border-slate-300 dark:border-gray-700 rounded px-2 py-1 text-[0.75rem] bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100"
            />
          </div>

          <div>
            <button
              type="button"
              onClick={onExport}
              disabled={!beginDate || busy}
              className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded disabled:opacity-40 hover:bg-blue-700"
            >
              {busy ? 'Exporting…' : 'Export'}
            </button>
          </div>

          {error && (
            <div className="px-3 py-2 text-[0.75rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              {error}
            </div>
          )}

          {done && (
            <div className="px-3 py-2 text-[0.75rem] text-slate-700 dark:text-gray-200 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded">
              Export downloaded.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SalesforceExportPage;
